import {
  Button, Card, Notice, Row, Spinner, Stack, areaBetween, brushWidth, cropOf, decodeImage, drawEdited,
  exportEdited, imagePoint, isArea, noSave, useI18n, useLimits,
  type Area, type EditSource, type PhotoEdit, type PhotoEditTool, type Point,
} from '@daghfal/core';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from 'react';

/** The tools, in the order a teacher reaches for them: hiding a face is the usual reason to open the editor. */
const TOOLS: readonly PhotoEditTool[] = ['blurArea', 'blurStroke', 'crop'];

export interface PhotoEditorProps {
  /** The photograph as chosen. It is drawn from and never sent. */
  file: File;
  /** The edits made so far, so that editing again carries on rather than starting over. */
  edits: readonly PhotoEdit[];
  /** The edits, and the photograph drawn from them; null when nothing is edited and the file goes as chosen. */
  onApply: (edits: PhotoEdit[], edited: Blob | null) => void;
  onCancel: () => void;
}

/** A drag under way: which pointer, where it started, and the brush's path so far. */
interface Gesture {
  pointerId: number;
  start: Point;
  points: Point[];
}

/**
 * Crop a photograph and blur the faces that must not show, before it is uploaded (Q1).
 *
 * The preview is the whole photograph with every blur drawn as it will be uploaded; a crop dims what it cuts
 * away rather than hiding it, so it can be redrawn. Drawing is by pointer only: a finger on a phone, which is
 * where photographs are taken. The photograph goes up only once the teacher applies the edit, and then only
 * as drawn (`exportEdited`).
 */
export function PhotoEditor({ file, edits: initial, onApply, onCancel }: PhotoEditorProps) {
  const { t } = useI18n();
  const { photoEditor: limits } = useLimits();

  const [source, setSource] = useState<EditSource | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [edits, setEdits] = useState<PhotoEdit[]>(() => [...initial]);
  const [tool, setTool] = useState<PhotoEditTool>('blurArea');
  // What the drag under way would add: a brush stroke is drawn into the preview as it goes, an area is outlined.
  const [pending, setPending] = useState<PhotoEdit | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [failed, setFailed] = useState(false);

  const frame = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const gesture = useRef<Gesture | null>(null);

  useEffect(() => {
    let current = true;
    setSource(null);
    setUnreadable(false);
    decodeImage(file)
      .then((image) => {
        if (current) setSource({ image, width: image.naturalWidth, height: image.naturalHeight });
      })
      .catch(() => {
        if (current) setUnreadable(true);
      });
    return () => {
      current = false;
    };
  }, [file]);

  const shown = useMemo(() => (pending?.kind === 'blurStroke' ? [...edits, pending] : edits), [edits, pending]);

  // The preview, at the size the screen shows it: drawing the full photograph on every stroke would be slow on
  // a phone, and the screen could not show the difference.
  useEffect(() => {
    const element = canvas.current;
    if (!element || !source) return;

    const available = (frame.current?.clientWidth ?? 0) * window.devicePixelRatio;
    const scale = available > 0 ? Math.min(1, available / source.width) : 1;
    element.width = Math.max(1, Math.round(source.width * scale));
    element.height = Math.max(1, Math.round(source.height * scale));

    const context = element.getContext('2d');
    if (!context) return;
    drawEdited(context, source, shown, { area: { x: 0, y: 0, width: source.width, height: source.height }, scale }, limits);
  }, [source, shown, limits]);

  if (unreadable) {
    return (
      <Card title={t('editor.title')}>
        <Stack>
          <Notice tone="warning">{t('editor.unreadable')}</Notice>
          <Row><Button variant="secondary" onClick={onCancel}>{t('editor.cancel')}</Button></Row>
        </Stack>
      </Card>
    );
  }

  if (!source) {
    return <Card title={t('editor.title')}><Spinner label={t('editor.loading')} /></Card>;
  }

  const brush = brushWidth(source.width, source.height, limits);
  const pointAt = (event: PointerEvent<HTMLCanvasElement>) =>
    imagePoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect(), source.width, source.height);

  const begin = (event: PointerEvent<HTMLCanvasElement>) => {
    if (preparing || gesture.current) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const start = pointAt(event);
    gesture.current = { pointerId: event.pointerId, start, points: [start] };
    setPending(tool === 'blurStroke' ? { kind: 'blurStroke', points: [start], width: brush } : null);
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const point = pointAt(event);
    if (tool === 'blurStroke') {
      current.points.push(point);
      setPending({ kind: 'blurStroke', points: [...current.points], width: brush });
    } else {
      setPending({ kind: tool, area: areaBetween(current.start, point, source.width, source.height) });
    }
  };

  const end = (event: PointerEvent<HTMLCanvasElement>) => {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    gesture.current = null;
    setPending(null);

    if (tool === 'blurStroke') {
      setEdits((done) => [...done, { kind: 'blurStroke', points: current.points, width: brush }]);
      return;
    }
    const area = areaBetween(current.start, pointAt(event), source.width, source.height);
    if (isArea(area, brush)) setEdits((done) => [...done, { kind: tool, area }]);
  };

  const abandon = () => {
    gesture.current = null;
    setPending(null);
  };

  const apply = () => {
    if (edits.length === 0) {
      onApply([], null);
      return;
    }
    setPreparing(true);
    setFailed(false);
    exportEdited(source, edits, limits)
      .then((edited) => onApply(edits, edited))
      .catch(() => {
        setFailed(true);
        setPreparing(false);
      });
  };

  // Positions on the stage, which runs left to right in both languages (see `.dg-editor__stage`).
  const place = (area: Area): CSSProperties => ({
    insetInlineStart: `${(area.x / source.width) * 100}%`,
    insetBlockStart: `${(area.y / source.height) * 100}%`,
    inlineSize: `${(area.width / source.width) * 100}%`,
    blockSize: `${(area.height / source.height) * 100}%`,
  });
  const cropped = edits.some((edit) => edit.kind === 'crop');
  const outline = pending && pending.kind !== 'blurStroke' ? pending.area : null;

  return (
    <Card title={t('editor.title')}>
      <Stack>
        {failed && <Notice tone="danger">{t('editor.failed')}</Notice>}

        <div role="group" aria-label={t('editor.tools')}>
          <Row>
            {TOOLS.map((name) => (
              <Button
                key={name}
                variant={tool === name ? 'primary' : 'secondary'}
                aria-pressed={tool === name}
                disabled={preparing}
                onClick={() => setTool(name)}
              >
                {t(`editor.tool.${name}`)}
              </Button>
            ))}
          </Row>
        </div>
        <p className="dg-muted dg-text-sm">{t(`editor.hint.${tool}`)}</p>

        <div ref={frame} className="dg-editor__frame">
          <div className="dg-editor__stage" dir="ltr">
            <canvas
              {...noSave}
              ref={canvas}
              className="dg-editor__canvas"
              aria-label={t('editor.canvas')}
              onPointerDown={begin}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={abandon}
            />
            {cropped && <div className="dg-editor__crop" style={place(cropOf(edits, source.width, source.height))} />}
            {outline && <div className="dg-editor__drag" style={place(outline)} />}
          </div>
        </div>

        <Row>
          <Button variant="ghost" disabled={edits.length === 0 || preparing} onClick={() => setEdits((done) => done.slice(0, -1))}>
            {t('editor.undo')}
          </Button>
          <Button variant="ghost" disabled={edits.length === 0 || preparing} onClick={() => setEdits([])}>
            {t('editor.reset')}
          </Button>
        </Row>
        <Row>
          <Button variant="secondary" disabled={preparing} onClick={onCancel}>{t('editor.cancel')}</Button>
          <Button busy={preparing} busyLabel={t('editor.preparing')} onClick={apply}>{t('editor.apply')}</Button>
        </Row>
      </Stack>
    </Card>
  );
}
